import { Map } from 'immutable'
import React from 'react'
import { Action, AsyncState, IOWidget, mk_widget, Pair } from 'widgets-for-react'
import { ImageSize, Link } from '../../../../cms/blocks/types.gen'
import { getClassName, link } from '../../../../cms/blocks/utils.gen'
import { ImageContainer } from '../../../../cms/blocks/widgets/ImageContainer.gen'
import { TemplateState } from '../../../../cms/page/types.gen'
import { CustomBlock } from '../../../../wordpress/types_and_parser/custom_blocks.gen'
import { Block } from '../../../../wordpress/types_and_parser/standard_blocks.gen'
import { Acf } from '../../../../wordpress/types_and_parser/types.mod'
import { PublicSite_CustomPageData } from '../../../application.mod'
import { CustomBlockData } from '../types.mod'

export type BSLSuperGridBlockProps<customBlockData, extra_images> = {
  items: Array<BSLSuperGridItemProps<customBlockData, extra_images>>
  title?: string
}

export type BSLSuperGridItemProps<customBlockData, extra_images> = {
  key?: string
  title: string
  subtitle: string
  description?: Acf.Wysiwyg
  image: number
  image_size: ImageSize<extra_images>
  image_alt: string
  link: Link | null
  download: string | null
  download_label: BSLSuperGridItemProps<customBlockData, extra_images>["download"]
  get_default_data_src: () => ImageSize<extra_images>
  get_default_data_srcset: () => Array<Pair<ImageSize<extra_images>, string>>
}

export type BSLSuperGridBlockStyle = Partial<{
  block_classname: string
  title_classname: string
  list_classname: string
  item_classname: string
  item_content_classname: string
  item_link_classname: string
  item_wrapper_classname: string
  item_download_classname: string
  item_subtitle_classname: string
  item_title_classname: string
  item_title_text_classname: string
  item_description_classname: string
  item_figure_classname: string
  item_image_classname: string
}>

export function BSLSuperGridBlock<customBlockData, extra_images>(
  props: BSLSuperGridBlockProps<customBlockData, extra_images>,
  options?: { style?: BSLSuperGridBlockStyle; key?: string }
): IOWidget<
  TemplateState<CustomBlockData, CustomBlock, Block, extra_images, PublicSite_CustomPageData>,
  Action<TemplateState<CustomBlockData, CustomBlock, Block, extra_images, PublicSite_CustomPageData>>
> {
  return (s: TemplateState<CustomBlockData, CustomBlock, Block, extra_images, PublicSite_CustomPageData>) =>
    mk_widget({
      run: _ => (
        <>
          <section
            className={getClassName(options, 'block_classname', 'block')}
            key={options && options.key ? options.key : ''}
          >
            {props.title && <h2 className={getClassName(options, 'title_classname', '')}>{props.title}</h2>}
            <ul className={getClassName(options, 'list_classname', '')}>
              {props.items.map(item => (
                <li
                  key={options.key && item && item.key ? `${options.key}-${item.key}` : ''}
                  className={getClassName(options, 'item_classname', '')}
                >
                  <div
                    className={getClassName(options, item.link.url ? 'item_link_classname' : 'item_wrapper_classname', '')}
                  >
                    <figure className={getClassName(options, 'item_figure_classname', ``)}>
                      {s.images.has(item.image)
                        ? ImageContainer(
                            s.images.get(item.image) as AsyncState<Map<ImageSize<extra_images>, string>>,
                            item.image_size,
                            getClassName(options, 'item_image_classname', 'w-100'),
                            item.image_alt,
                            item.get_default_data_src,
                            item.get_default_data_srcset
                          ).run(_ => {})
                        : null}
                    </figure>

                    <div className={getClassName(options, 'item_content_classname', '')}>
                      <p className={getClassName(options, 'item_subtitle_classname', '')}>{item.subtitle}</p>
                      {item.link.url ? (
                        link(
                          item.link,
                          { classname: '' },
                          <h3 className={getClassName(options, 'item_title_classname', '')}>
                            <span className={getClassName(options, 'item_title_text_classname', '')}>{item.title}</span>
                          </h3>
                        )
                      ) : (
                        <h3 className={getClassName(options, 'item_title_classname', '')}>
                          <span className={getClassName(options, 'item_title_text_classname', '')}>{item.title}</span>
                        </h3>
                      )}
                      {item.description && (
                        <div
                          className={getClassName(options, 'item_description_classname', '')}
                          dangerouslySetInnerHTML={{ __html: item.description }}
                        />
                      )}
                      {item.download && (
                        <a
                          className={getClassName(options, 'item_download_classname', '')}
                          href={item.download}
                          target="_blank"
                        >
                          <svg aria-hidden="true" width="13" height="13">
                            <use xlinkHref="#icon--download" />
                          </svg>
                          <span>{item.download_label}</span>
                        </a>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </>
      )
    })
}
